/**
 * admin.posture.vision data layer. Moved here from the consumer frontend
 * (src/lib/admin-data.ts) on 2026-09-18 so the admin has one home; the
 * consumer app's /admin now redirects here. REAL DATA ONLY: every function
 * reads a pv-auth endpoint, nothing is estimated and nothing is mocked.
 * Endpoints (pv-auth, server-side SUPER_ADMIN rbac):
 *   GET  /provider/admin/overview                         → console metrics
 *   GET  /provider/admin/applications?status=all          → applications
 *   GET  /provider/admin/applications/:id/credential-url  → signed PDF URL
 *   POST /provider/admin/applications/:id/decision        → approve/reject + email
 *   GET  /provider/admin/demand                           → demand ranking
 */

export interface AdminOverview {
  consumersTotal: number;
  consumersActive7d: number;
  providersActive: number;
  applicationsPending: number;
  /** Monthly recurring revenue, USD (annual plans normalized to /12). */
  mrrUsd: number;
  patientsLinked: number;
}

/** Live console metrics, computed by the backend from the DB. */
export async function fetchAdminOverview(): Promise<AdminOverview> {
  const { apiGet } = await import('@/services/base');
  const raw = (await apiGet('/provider/admin/overview', true)) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    consumersTotal: num(raw?.consumersTotal),
    consumersActive7d: num(raw?.consumersActive7d),
    providersActive: num(raw?.providersActive),
    applicationsPending: num(raw?.applicationsPending),
    mrrUsd: num(raw?.mrrUsd),
    patientsLinked: num(raw?.patientsLinked),
  };
}


/** The real onboarding state machine (PROVIDER_ONBOARDING_BILLING.md §2). */
export type ApplicationStatus =
  | 'under_review' | 'approved' | 'active' | 'rejected' | 'expired';

export const APP_STATUS_LABEL: Record<ApplicationStatus, string> = {
  under_review: 'Under review',
  approved: 'Approved · awaiting payment',
  active: 'Active',
  rejected: 'Rejected',
  expired: 'Expired',
};

export interface ApplicationIntake {
  practiceType?: string;
  patientVolume?: string;
  intendedUse: string[];
  customInterest?: string;
  notes?: string;
}

/** The full onboarding record behind a queue row (expanded detail view). */
export interface ApplicationDetail {
  intake: ApplicationIntake;
  /** ISO timestamps, the list column shows a relative label instead. */
  submittedAtIso?: string;
  approvedAtIso?: string;
  payWindowEndsAtIso?: string;
  rejectionReason?: string;
  plan?: 'provider_monthly' | 'provider_annual';
  seatBillingMode?: 'clinic_pays' | 'patient_pays';
}

export interface ProviderApplicationRow {
  id: string;
  practiceName: string;
  applicantName: string;
  email: string;
  phone?: string;
  credentialNumber: string;
  credentialFileName?: string;
  practiceType: string;
  status: ApplicationStatus;
  submittedAt: string;
  detail: ApplicationDetail;
}

const STATUSES: ApplicationStatus[] = [
  'under_review', 'approved', 'active', 'rejected', 'expired',
];

function relLabel(iso: string | undefined): string {
  if (!iso) return ', ';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (Number.isNaN(d)) return ', ';
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d}d ago`;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

function normalizeApplication(raw: Record<string, unknown>): ProviderApplicationRow {
  const intake = (raw.intake ?? {}) as Record<string, unknown>;
  return {
    id: String(raw.id ?? ''),
    practiceName: String(raw.practiceName ?? '-'),
    applicantName: `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim() || '-',
    email: String(raw.email ?? '-'),
    phone: str(raw.phone),
    credentialNumber: String(raw.credentialNumber ?? '-'),
    credentialFileName: str(raw.credentialFileName),
    practiceType: String(intake.practiceType ?? '-'),
    status: STATUSES.includes(raw.status as ApplicationStatus) ? (raw.status as ApplicationStatus) : 'under_review',
    submittedAt: relLabel((raw.submittedAt ?? raw.createdAt) as string | undefined),
    detail: {
      intake: {
        practiceType: str(intake.practiceType),
        patientVolume: str(intake.patientVolume),
        intendedUse: Array.isArray(intake.intendedUse) ? (intake.intendedUse as string[]) : [],
        customInterest: str(intake.customInterest),
        notes: str(intake.notes),
      },
      submittedAtIso: str(raw.submittedAt),
      approvedAtIso: str(raw.approvedAt),
      payWindowEndsAtIso: str(raw.payWindowEndsAt),
      rejectionReason: str(raw.rejectionReason),
      plan: raw.plan === 'provider_monthly' || raw.plan === 'provider_annual' ? raw.plan : undefined,
      seatBillingMode: raw.seatBillingMode === 'clinic_pays' || raw.seatBillingMode === 'patient_pays' ? raw.seatBillingMode : undefined,
    },
  };
}

export async function fetchProviderApplications(): Promise<ProviderApplicationRow[]> {
  const { apiGet } = await import('@/services/base');
  const res = await apiGet('/provider/admin/applications?status=all&pageSize=100', true);
  // pv-auth is moving this endpoint from a bare array to an
  // { applications, pagination } envelope. Read both so this console keeps
  // working either side of that deploy, in either order.
  const raw: unknown = Array.isArray(res)
    ? res
    : (res as { applications?: unknown } | null)?.applications;
  return (Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []).map(normalizeApplication);
}

/** Approve/reject; returns the new status. Backend emails the applicant. */
export async function decideApplication(
  id: string,
  approve: boolean,
  reason?: string,
): Promise<ApplicationStatus> {
  const { apiPost } = await import('@/services/base');
  const res = (await apiPost(`/provider/admin/applications/${id}/decision`, { approve, reason }, true)) as { status?: string };
  return STATUSES.includes(res?.status as ApplicationStatus)
    ? (res.status as ApplicationStatus)
    : approve ? 'approved' : 'rejected';
}

/** Short-lived signed URL for the (private) credential PDF. */
export async function fetchCredentialUrl(id: string): Promise<string | null> {
  const { apiGet } = await import('@/services/base');
  const res = (await apiGet(`/provider/admin/applications/${id}/credential-url`, true)) as { url?: unknown };
  return typeof res?.url === 'string' ? res.url : null;
}

/* ── Demand view (spec PART 4) ──────────────────────────────────────────
 * What clinics ask for that we haven't built: custom-exercise requests merged
 * with provider-recorded follow-along clips, ranked by frequency then recency,
 * so the scored-exercise backlog can be prioritized. */

export interface DemandItem {
  title: string;
  count: number;
  customRequests: number;
  followAlongs: number;
  clinics: string[];
  latestAt: string; // relative label
  latestClinic: string;
}

export interface AdminDemand {
  items: DemandItem[];
  totals: { customRequests: number; followAlongs: number; distinctMovements: number };
  /** True when the backend truncated the ranking to its limit (default 50). */
  hasMore: boolean;
}

export async function fetchAdminDemand(): Promise<AdminDemand> {
  const { apiGet } = await import('@/services/base');
  const raw = (await apiGet('/provider/admin/demand', true)) as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const totals = (raw?.totals ?? {}) as Record<string, unknown>;
  const items = Array.isArray(raw?.items) ? (raw.items as Record<string, unknown>[]) : [];
  return {
    items: items.map((i) => ({
      title: String(i.title ?? '-'),
      count: num(i.count),
      customRequests: num(i.customRequests),
      followAlongs: num(i.followAlongs),
      clinics: Array.isArray(i.clinics) ? (i.clinics as string[]) : [],
      latestAt: relLabel(str(i.latestAt)),
      latestClinic: String(i.latestClinic ?? '-'),
    })),
    totals: {
      customRequests: num(totals.customRequests),
      followAlongs: num(totals.followAlongs),
      distinctMovements: num(totals.distinctMovements),
    },
    hasMore: Boolean((raw?.pagination as Record<string, unknown> | undefined)?.hasMore),
  };
}

/* ── Growth panel: signups, verified, first checks, payments ─────────── */
export interface GrowthWindow { signups: number; verified: number; firstChecks: number; paid: number }
export type GrowthState = 'paid' | 'abandoned_checkout' | 'checked' | 'signed_up' | 'unverified';
export interface GrowthPerson {
  userId: string; email: string; name: string; firstName: string; phone: string | null; createdAt: string;
  /** Same instant as createdAt, named for what it is: when they signed up. */
  signupAt: string;
  verified: boolean;
  checks: number;
  /** When they first measured, and when they last did (null when never). */
  firstCheckAt: string | null;
  lastCheckAt: string | null; lastScore: number | null;
  plan: string | null; interval: string | null; status: string | null; ghlUrl: string | null;
  state: GrowthState; source: string | null; medium: string | null; campaign: string | null; promoterCode: string | null; timezone: string | null;
  journey: string[]; feedbackCount: number; checkoutOpenedAt: string | null;
  outreach: { channel: string; status: string; at: string; subject: string | null }[];
  draft: { channel: 'email' | 'sms'; subject: string; body: string };
}
export interface GrowthPayment {
  userId: string; email: string; name: string; plan: string; interval: string | null; priceUsd: number | null;
  /** What Stripe actually charged, null when Stripe showed no payment; source says which Stripe object it came from. */
  paidUsd: number | null; paidSource: 'invoice' | 'checkout_session' | 'customer_invoice' | null;
  coupon: string | null;
  status: string; paidAt: string; periodEnd: string; ghlUrl: string | null;
}
export interface AdminGrowth {
  generatedAt: string; today: GrowthWindow; last7d: GrowthWindow; allTime: GrowthWindow & { activeSubscriptions: number };
  recentSignups: GrowthPerson[]; recentPayments: GrowthPayment[];
}
/** GET /provider/admin/growth: row counts and rows, refreshed on every open. */
export async function fetchAdminGrowth(): Promise<AdminGrowth> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/growth', true)) as AdminGrowth;
}
/** POST /provider/admin/growth/outreach: send one approved note through GHL. */
export async function sendGrowthOutreach(input: { userId: string; channel: 'email' | 'sms'; subject?: string; body: string }): Promise<{ id: string; status: 'sent' | 'queued' | 'failed'; via?: 'ghl' | 'mailer'; error?: string; message?: string }> {
  const { apiPost } = await import('@/services/base');
  return (await apiPost('/provider/admin/growth/outreach', input, true)) as { id: string; status: 'sent' | 'queued' | 'failed'; via?: 'ghl' | 'mailer'; error?: string; message?: string };
}

/* ── Person journal: what one person did, and what Elina said ────────── */
//   GET  /provider/admin/journal/:userId            → one page, newest first
//   GET  /provider/admin/journal/:userId/export.csv → the same rows, flattened
//   POST /provider/admin/journal/:userId/notes      → how Elina should answer them
// SUPER_ADMIN only, rate limited, and every read is logged server side.

export type { JournalEntry, JournalKind, JournalNote, JournalPage } from './person-journal';

/** One page of the timeline. `cursor` comes from the previous page's nextCursor. */
export async function fetchPersonJournal(input: { userId: string; cursor?: string | null; kinds?: string[] | null }): Promise<import('./person-journal').JournalPage> {
  const { apiGet } = await import('@/services/base');
  const params = new URLSearchParams();
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.kinds && input.kinds.length) params.set('kinds', input.kinds.join(','));
  const qs = params.toString();
  const raw = (await apiGet(`/provider/admin/journal/${encodeURIComponent(input.userId)}${qs ? `?${qs}` : ''}`, true)) as import('./person-journal').JournalPage;
  return {
    userId: raw?.userId ?? input.userId,
    person: raw?.person ?? null,
    entries: Array.isArray(raw?.entries) ? raw.entries : [],
    nextCursor: raw?.nextCursor ?? null,
    unavailable: Array.isArray(raw?.unavailable) ? raw.unavailable : [],
    notes: Array.isArray(raw?.notes) ? raw.notes : [],
  };
}

/** POST a standing note about this person, for Elina to be taught from. */
export async function saveJournalNote(input: { userId: string; body: string }): Promise<import('./person-journal').JournalNote> {
  const { apiPost } = await import('@/services/base');
  return (await apiPost(`/provider/admin/journal/${encodeURIComponent(input.userId)}/notes`, { body: input.body }, true)) as import('./person-journal').JournalNote;
}

/**
 * Pull the CSV and hand it to the browser. It goes through fetch rather than a
 * plain link because the endpoint needs the bearer token, and a link cannot
 * carry one.
 */
export async function downloadJournalCsv(userId: string, filename?: string): Promise<void> {
  const { BASE_URL, authHeaders } = await import('@/services/base');
  const res = await fetch(`${BASE_URL}/provider/admin/journal/${encodeURIComponent(userId)}/export.csv`, {
    method: 'GET',
    headers: { ...authHeaders(), 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    mode: 'cors',
  });
  if (!res.ok) throw new Error(`The export came back ${res.status}.`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `journal-${userId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── Product digest: the numbers the Slack post carries ─────────────────── */
//   GET /provider/admin/digest (SUPER_ADMIN)
export interface FunnelCounts { signups: number; verified: number; firstCheck: number; thirdCheckDay: number; lookViewed: number; checkoutOpened: number | null; paid: number; paidMonthly: number; paidAnnual: number }
export interface DigestProposal { rule: string; text: string; evidence: Record<string, number | string | null> }
export interface StopBucket { step: string; count: number; people: { userId: string; firstName: string; url: string }[] }
export interface SurfaceUsage { key: string; label: string; users: number; events: number; minutes?: number | null }
export interface ErrorCounts { failedChecks: number; abortedChecks: number; failedCheckReasons: { reason: string; count: number }[]; failedOutreach: number; sms429: number; ticketsOpened: number }
export interface ProductDigest {
  generatedAt: string;
  windows: { last24h: string; last7d: string; prior7d: string };
  funnel: { last24h: FunnelCounts; last7d: FunnelCounts; prior7d: FunnelCounts; biggestDrop24h: { from: string; to: string; lost: number; pct: number } | null; biggestDrop7d: { from: string; to: string; lost: number; pct: number } | null; paidEvents: { last24h: { total: number; monthly: number; annual: number }; last7d: { total: number; monthly: number; annual: number } } };
  stops: { cohort: string; stallDays: number; total: number; buckets: StopBucket[] };
  usage: { windowDays: number; surfaces: SurfaceUsage[]; leastUsed: string[] };
  errors: { last24h: ErrorCounts; last7d: ErrorCounts; ticketsUnansweredOver24h: number; sentry: { issues: { title: string; count: number; url?: string; lastSeen?: string }[] } | null; sentryReason: string | null };
  proposals: DigestProposal[];
}
export async function fetchProductDigest(): Promise<ProductDigest> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/digest', true)) as ProductDigest;
}

/* ── Console feeds (pv-auth PR #292) ────────────────────────────────────── */
//   GET /provider/admin/flags            → the flags the process runs with
//   GET /provider/admin/messages         → outreach history, queue, journey steps
//   GET /provider/admin/support-tickets  → tickets by status
//   GET /provider/admin/usage?ids=       → per-person usage and asks
export interface FlagReading { key: string; on: boolean; raw: string | null; rule: 'on_when_true' | 'off_when_false'; what: string }
export interface AdminFlags {
  generatedAt: string; environment: string; flags: FlagReading[];
  digest: { enabled: boolean; hourUtc: number; channels: string[] };
  slack: { supportChannelId: string | null; growthChannelId: string | null; productChannelId: string | null };
  ghl: { locationId: string | null };
}
export async function fetchAdminFlags(): Promise<AdminFlags> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/flags', true)) as AdminFlags;
}

export interface OutreachRow { id: string; userId: string; email: string; name: string; phone: string | null; ghlUrl: string | null; channel: string; state: string; subject: string | null; body: string; status: string; error: string | null; sentBy: string | null; createdAt: string }
export interface JourneyRow { id: string; userId: string; email: string; name: string; step: string; channel: string; sentAt: string }
export interface AdminMessages { generatedAt: string; counts: { queued: number; sent7d: number; failed7d: number }; outreach: OutreachRow[]; journey: JourneyRow[] }
export async function fetchAdminMessages(limit?: number): Promise<AdminMessages> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/messages${limit ? `?limit=${limit}` : ''}`, true)) as AdminMessages;
}

export interface TicketRow { id: string; userId: string | null; email: string | null; name: string | null; source: string; subject: string; status: string; createdAt: string; updatedAt: string; ageHours: number; ghlContactUrl: string | null; ghlConversationUrl: string | null }
export interface AdminTickets { generatedAt: string; status: string; counts: { new: number; answered: number; fixed: number; unansweredOver24h: number }; tickets: TicketRow[] }
export async function fetchAdminTickets(status: 'new' | 'answered' | 'fixed' | 'all' = 'new'): Promise<AdminTickets> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/support-tickets?status=${status}`, true)) as AdminTickets;
}

export interface PersonUsage {
  active: { sessions: number; minutes: number | null; lastAt: string | null };
  exercises: { count: number; lastAt: string | null };
  rom: { count: number; lastAt: string | null };
  asks: { supportTickets: { count: number; lastAt: string | null }; helpRequests: { count: number; lastAt: string | null }; elinaMessages: { count: number; lastAt: string | null }; reflections: { count: number; lastAt: string | null }; lastAt: string | null };
}
export interface AdminUsage { generatedAt: string; people: Record<string, PersonUsage> }
/** At most 300 ids a call; the caller chunks a longer list. */
export async function fetchAdminUsage(ids: string[]): Promise<AdminUsage> {
  const { apiGet } = await import('@/services/base');
  const out: AdminUsage = { generatedAt: new Date().toISOString(), people: {} };
  for (let i = 0; i < ids.length; i += 300) {
    const page = (await apiGet(`/provider/admin/usage?ids=${encodeURIComponent(ids.slice(i, i + 300).join(','))}`, true)) as AdminUsage;
    out.generatedAt = page.generatedAt;
    Object.assign(out.people, page.people);
  }
  return out;
}

/* ── Admins (read only) ─────────────────────────────────────────────────── */
//   GET /admin/admins
export interface AdminAccount { id: string; email: string; firstName: string | null; lastName: string | null; role: string; createdAt: string; isSuspended?: boolean; twoFactorEnabled?: boolean }
export async function fetchAdmins(): Promise<AdminAccount[]> {
  const { apiGet } = await import('@/services/base');
  const raw = (await apiGet('/admin/admins', true)) as unknown;
  const list = Array.isArray(raw) ? raw : Array.isArray((raw as { data?: unknown })?.data) ? (raw as { data: unknown[] }).data : [];
  return list as AdminAccount[];
}
